import { Controller } from "@hotwired/stimulus";

// Click-to-pin annotator for showcase photos. Powers the "Showcase
// callouts" section on the custom-figure edit page.
//
// Behavior:
//   * Click anywhere on the image (in empty space) → POST a new
//     annotation at the (x, y) fraction of the displayed image box.
//     The server responds with a Turbo Stream that replaces this
//     element with a refreshed annotator (new pin + picker visible).
//   * Drag a pin → live preview while dragging, then PATCH the
//     final (x, y) on mouseup. We avoid mid-drag PATCHes to keep
//     network chatter down on slow connections.
//   * Click a pin (no drag) → no-op; the per-pin form sitting beside
//     the canvas is where tags get edited.
//
// Markup contract:
//
//   <div data-controller="photo-annotator"
//        data-photo-annotator-create-url-value="/.../annotations">
//     <div data-photo-annotator-target="canvas">
//       <img data-action="click->photo-annotator#imageClick">
//       <button data-photo-annotator-target="pin"
//               data-annotation-id="42"
//               data-update-url="/.../annotations/42"
//               data-action="mousedown->photo-annotator#startDrag click->photo-annotator#swallowClick"
//               style="left: 50%; top: 50%;">…</button>
//     </div>
//   </div>
export default class extends Controller {
  static targets = ["canvas", "pin"];
  static values  = { createUrl: String };

  // Tracks the in-flight drag (if any) and whether the most recent
  // mouseup followed real movement — used so a clean click on a pin
  // doesn't get treated as a drag and doesn't bubble up as an
  // "image clicked here" new-pin event.
  connect() {
    this._drag = null;
  }

  // Image click: drop a new pin. Computes (x, y) relative to the
  // image's rendered box so the stored fractions are independent of
  // which variant size happened to load.
  async imageClick(event) {
    // Pins live inside the canvas as siblings of the image; if the
    // click landed on a pin its own handlers (or swallowClick below)
    // will have stopped propagation already. Belt-and-braces here.
    if (event.target.closest("[data-photo-annotator-target='pin']")) return;

    const img  = event.currentTarget;
    const rect = img.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const x = clamp01((event.clientX - rect.left) / rect.width);
    const y = clamp01((event.clientY - rect.top)  / rect.height);

    const form = new FormData();
    form.append("annotation[x]", x);
    form.append("annotation[y]", y);

    await this._send(this.createUrlValue, "POST", form);
  }

  // Drag start: latch the pin + record the canvas rect so mousemove
  // can convert pointer position to image-fraction. We bind the
  // mousemove/mouseup handlers on window so a quick drift outside the
  // canvas doesn't drop the drag.
  startDrag(event) {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    const pin = event.currentTarget;
    const canvas = pin.closest("[data-photo-annotator-target='canvas']");
    const img    = canvas?.querySelector("[data-pin-anchor]");
    if (!img) return;

    const rect = img.getBoundingClientRect();
    this._drag = {
      pin,
      img,
      rect,
      moved:   false,
      startX:  event.clientX,
      startY:  event.clientY,
      lastX:   parseFloat(pin.dataset.x || "0"),
      lastY:   parseFloat(pin.dataset.y || "0"),
    };

    this._boundMove = this._onMove.bind(this);
    this._boundUp   = this._onUp.bind(this);
    window.addEventListener("mousemove", this._boundMove);
    window.addEventListener("mouseup",   this._boundUp);

    pin.classList.add("ring-4", "ring-yellow-300/40");
  }

  // Pins absorb their own click so it doesn't bubble through to the
  // image's click handler (which would otherwise drop a new pin on
  // top of the one we just released).
  swallowClick(event) {
    event.stopPropagation();
  }

  _onMove(event) {
    const drag = this._drag;
    if (!drag) return;

    if (!drag.moved && (Math.abs(event.clientX - drag.startX) > 2 || Math.abs(event.clientY - drag.startY) > 2)) {
      drag.moved = true;
    }
    if (!drag.moved) return;

    const x = clamp01((event.clientX - drag.rect.left) / drag.rect.width);
    const y = clamp01((event.clientY - drag.rect.top)  / drag.rect.height);
    drag.lastX = x;
    drag.lastY = y;
    drag.pin.style.left = `${(x * 100).toFixed(3)}%`;
    drag.pin.style.top  = `${(y * 100).toFixed(3)}%`;
    // A label being dragged is centred on the pointer from now on.
    if (drag.pin.dataset.dragKind === "label") drag.pin.classList.add("is-placed");
  }

  async _onUp() {
    const drag = this._drag;
    this._teardownDrag();
    if (!drag) return;
    drag.pin.classList.remove("ring-4", "ring-yellow-300/40");

    if (!drag.moved) return;

    const url = drag.pin.dataset.updateUrl;
    if (!url) return;

    // A pin moves itself; a label (data-drag-kind="label") moves its label.
    const key = drag.pin.dataset.dragKind === "label" ? "label_" : "";
    const form = new FormData();
    form.append(`annotation[${key}x]`, drag.lastX);
    form.append(`annotation[${key}y]`, drag.lastY);
    form.append("_method", "patch");
    await this._send(url, "POST", form);
  }

  _teardownDrag() {
    if (this._boundMove) window.removeEventListener("mousemove", this._boundMove);
    if (this._boundUp)   window.removeEventListener("mouseup",   this._boundUp);
    this._boundMove = null;
    this._boundUp   = null;
    this._drag = null;
  }

  // Submits the form via fetch with `Accept: text/vnd.turbo-stream.html`
  // so the response replaces our annotator partial in place.
  async _send(url, method, formData) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Accept":       "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken,
        },
        body: formData,
      });
      if (!response.ok && response.status !== 422) {
        console.error("Annotator request failed", response.status);
        return;
      }
      const html = await response.text();
      if (window.Turbo?.renderStreamMessage) {
        window.Turbo.renderStreamMessage(html);
      }
    } catch (err) {
      console.error("Annotator request error", err);
    }
  }
}

function clamp01(v) {
  if (Number.isNaN(v)) return 0;
  return Math.max(0, Math.min(1, v));
}
